
import type { AltitudeMode, TurnType } from '@/types/mission.types'
import { RuntimeError } from 'cesium'
import type { Mission, BaseMissionElement, MissionElementType } from '@/types/route.types'
import log from './useLogger'
import { Cartesian3 } from 'cesium'

/**
 * Reconstructs Cesium objects like Cartesian3 from a serialized state
 * to be used after state is loaded from storage
 * @param routes The routes array from the store
 * @returns Routes with proper Cesium objects
 */
export function reconstructCesiumObjects(routes: Mission[]): Mission[] {
  if (!routes || !Array.isArray(routes)) return []

  try {
    return routes.map((route) => {
      try {
        // Make a copy of the route to avoid mutating the original
        const routeCopy = { ...route }

        // Reconstruct waypoint objects
        if (routeCopy.elements && Array.isArray(routeCopy.elements)) {
          routeCopy.elements = routeCopy.elements.map((element) => {
            try {
              const elementCopy = { ...element }

              // Reconstruct position Cartesian3
              if (elementCopy.position && (elementCopy.position as any).__type === 'Cartesian3') {
                try {
                  const pos = elementCopy.position as any
                  if (
                    typeof pos.x === 'number' &&
                    typeof pos.y === 'number' &&
                    typeof pos.z === 'number'
                  ) {
                    elementCopy.position = new Cartesian3(pos.x, pos.y, pos.z)
                  } else {
                    log.warn(
                      `Invalid Cartesian3 data for waypoint position: ${JSON.stringify(pos)}`,
                    )
                    throw new RuntimeError('Invalid position data')
                  }
                } catch (error) {
                  log.error('Error reconstructing position:', error)
                  if (error instanceof RuntimeError) {
                    // Propagate Cesium errors for better handling up the chain
                    throw error
                  }
                  // Create an emergency position at 0,0,0 to prevent further errors
                  elementCopy.position = new Cartesian3(0, 0, 0)
                }
              }

              // Reconstruct topPosition Cartesian3
              if (
                elementCopy.topPosition &&
                (elementCopy.topPosition as any).__type === 'Cartesian3'
              ) {
                try {
                  const pos = elementCopy.topPosition as any
                  if (
                    typeof pos.x === 'number' &&
                    typeof pos.y === 'number' &&
                    typeof pos.z === 'number'
                  ) {
                    elementCopy.topPosition = new Cartesian3(pos.x, pos.y, pos.z)
                  } else {
                    log.warn(
                      `Invalid Cartesian3 data for waypoint topPosition: ${JSON.stringify(pos)}`,
                    )
                    throw new RuntimeError('Invalid topPosition data')
                  }
                } catch (error) {
                  log.error('Error reconstructing topPosition:', error)
                  if (error instanceof RuntimeError) {
                    // Propagate Cesium errors for better handling up the chain
                    throw error
                  }
                  // Create an emergency position slightly above the default position
                  elementCopy.topPosition = new Cartesian3(0, 0, 10)
                }
              }

              return elementCopy
            } catch (error) {
              // Log and handle errors during waypoint reconstruction
              if (error instanceof RuntimeError) {
                log.error(
                  `Cesium RuntimeError during waypoint reconstruction: ${error.message}`,
                )
                log.error(`Stack: ${error.stack}`)
              } else {
                log.error('Error reconstructing waypoint:', error)
              }
              // Return a minimal valid waypoint to prevent app crash
              return {
                id: element.id || 'error-waypoint',
                type: 'waypoint' as MissionElementType,
                position: new Cartesian3(0, 0, 0),
                topPosition: new Cartesian3(0, 0, 10),
                longitude: 0,
                latitude: 0,
                altitude: 0,
                speed: 0,
                radius: 0,
                altitudeMode: 'ASML' as AltitudeMode,
                turnType: 'Point and stop discon' as TurnType,
                height: 0,
                actions: [],
              } as BaseMissionElement
            }
          })
        }

        return routeCopy
      } catch (error) {
        // Handle errors during route processing
        if (error instanceof RuntimeError) {
          log.error(`Cesium RuntimeError processing route: ${error.message}`)
          log.error(`Stack: ${error.stack}`)
        } else {
          log.error('Error processing route:', error)
        }
        // Return a minimal valid route to prevent app crash
        return { ...route, waypoints: [] }
      }
    })
  } catch (error) {
    // Handle top-level errors
    if (error instanceof RuntimeError) {
      log.error(`Cesium RuntimeError in reconstructCesiumObjects: ${error.message}`)
      log.error(`Stack: ${error.stack}`)
    } else {
      log.error('Error in reconstructCesiumObjects:', error)
    }
    // Return an empty array to prevent app crash
    return []
  }
}

/**
 * Creates a deep clone of an object, replacing Cesium objects with serializable versions
 * @param obj The object to clone
 * @returns A serializable version of the object
 */
export function createSerializableClone(obj: any): any {
  if (!obj) return obj

  try {
    // Handle primitive types
    if (typeof obj !== 'object' || obj === null) return obj

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map((item) => {
        try {
          return createSerializableClone(item)
        } catch (error) {
          // Handle errors during array item serialization
          if (error instanceof RuntimeError) {
            log.error(`Cesium RuntimeError serializing array item: ${error.message}`)
          } else {
            log.error('Error serializing array item:', error)
          }
          // Return a marker for the error
          return { error: 'Serialization failed' }
        }
      })
    }

    // Handle Cartesian3
    if (obj.constructor && obj.constructor.name === 'Cartesian3') {
      try {
        return {
          __type: 'Cartesian3',
          x: obj.x,
          y: obj.y,
          z: obj.z,
        }
      } catch (error) {
        if (error instanceof RuntimeError) {
          log.error(`Cesium RuntimeError serializing Cartesian3: ${error.message}`)
          // Rethrow for handling at a higher level
          throw error
        }
        log.error('Error serializing Cartesian3:', error)
        // Return a properly formed but empty Cartesian3
        return { __type: 'Cartesian3', x: 0, y: 0, z: 0 }
      }
    }

    // Handle other Cesium objects that need special handling
    if (obj.constructor && obj.constructor.name === 'Entity') {
      try {
        return { __type: 'Entity', id: obj.id }
      } catch (error) {
        if (error instanceof RuntimeError) {
          log.error(`Cesium RuntimeError serializing Entity: ${error.message}`)
          throw error
        }
        log.error('Error serializing Entity:', error)
        return { __type: 'Entity', id: 'error-entity' }
      }
    }

    // Handle regular objects
    const result: any = {}
    for (const key in obj) {
      // Skip functions and internal properties
      if (typeof obj[key] === 'function' || key.startsWith('_')) continue

      try {
        // Handle special properties
        if (key === 'waypointEntities' && Array.isArray(obj[key])) {
          result[key] = obj[key].map((entity: any) => {
            try {
              return {
                id: entity.id,
                ellipsoidEntity: entity.ellipsoidEntity,
                verticalLineEntity: entity.verticalLineEntity,
                ellipseEntity: entity.ellipseEntity,
              }
            } catch (error) {
              if (error instanceof RuntimeError) {
                log.error(`Cesium RuntimeError serializing entity: ${error.message}`)
              }
              return { id: entity.id || 'error-entity' }
            }
          })
          continue
        }

        // Recursively handle other properties
        result[key] = createSerializableClone(obj[key])
      } catch (error) {
        if (error instanceof RuntimeError) {
          log.error(`Cesium RuntimeError serializing property ${key}: ${error.message}`)
        } else {
          log.error(`Error serializing property ${key}:`, error)
        }
        // Skip the problematic property
        result[key] = null
      }
    }

    return result
  } catch (error) {
    if (error instanceof RuntimeError) {
      log.error(`Cesium RuntimeError in createSerializableClone: ${error.message}`)
      log.error(`Stack: ${error.stack}`)
    } else {
      log.error('Error in createSerializableClone:', error)
    }
    // Return a minimal object with error info to prevent app crash
    return {
      error: 'Serialization failed',
      errorType: error instanceof RuntimeError ? 'RuntimeError' : 'Unknown',
    }
  }
}

/**
 * Ensures an object is serializable for Web Workers by converting Cesium objects to plain objects
 * @param obj The object to make serializable
 * @returns A serializable version of the object
 */
export function makeSerializableForWorkers(obj: any): any {
  if (!obj) return obj

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item) => makeSerializableForWorkers(item))
  }

  // Handle Cartesian3
  if (obj.constructor && obj.constructor.name === 'Cartesian3') {
    return {
      x: obj.x,
      y: obj.y,
      z: obj.z,
    }
  }

  // Handle Entity
  if (obj.constructor && obj.constructor.name === 'Entity') {
    return {
      id: obj.id,
      name: obj.name,
    }
  }

  // Handle regular objects
  if (typeof obj === 'object') {
    const result: any = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = makeSerializableForWorkers(obj[key])
      }
    }
    return result
  }

  // Return primitives as is
  return obj
}
